import React, { useState, useEffect } from 'react'
import { Card, Spin, Button, Form, Input, Divider, message, Space, Typography, notification } from 'antd'
import { LoadingOutlined, WindowsOutlined, UserOutlined, LockOutlined, SwapOutlined } from '@ant-design/icons'
import { useMsal } from '@azure/msal-react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { loginRequest } from '../../config/authConfig'
import { setUser } from './authSlice'
import authService from '../../api/authService'

const { Title, Text } = Typography

const Login = () => {
  const { instance } = useMsal()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useSelector((state) => state.auth)
  const [isLoading, setIsLoading] = useState(false)
  const [loginMethod, setLoginMethod] = useState('auto') // 'auto', 'microsoft', 'manual'
  const [isManualMethod, setIsManualMethod] = useState(false) // Track if coming from "login with different user"
  const [form] = Form.useForm()

  // If an MSAL account is already cached (e.g. user returned to /login with
  // an existing AAD session and Redux is empty), complete the backend login
  // silently without forcing another AAD redirect.
  const processActiveAccount = async () => {
    try {
      const account = instance.getActiveAccount() || instance.getAllAccounts()[0]
      if (!account) return false

      setIsLoading(true)
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      })

      const apiResponse = await authService.loginUserAzure(tokenResponse.accessToken)
      if (
        !apiResponse.requestIsSuccess ||
        !apiResponse.isAuthenticated ||
        !apiResponse.userHasAccess
      ) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('user')
        setIsLoading(false)
        navigate('/unauthorized', {
          state: {
            message:
              apiResponse.requestMessage ||
              'User is not authenticated or does not have access to this application',
          },
        })
        // Truthy so handleMicrosoftLogin stops and does not trigger an AAD redirect.
        return 'denied'
      }

      if (apiResponse.tokenID) {
        localStorage.setItem('access_token', apiResponse.tokenID)
      }
      dispatch(setUser({
        username: apiResponse.userEmailID || account.username,
        name: apiResponse.fullName || account.name,
        email: apiResponse.userEmailID || account.username,
        id: apiResponse.userGPN || account.homeAccountId,
        token: apiResponse.tokenID,
        authType: 'microsoft',
        role: apiResponse.userCurrentRole,
        roles: apiResponse.userAppRoles,
      }))
      navigate('/dashboard', { replace: true })
      return true
    } catch (err) {
      // Silent acquisition or backend call failed; caller will trigger interactive redirect
      console.warn('Silent active-account login failed:', err)
      const status = err?.response?.status
      // Backend denied access (401/403) - go to unauthorized, do NOT retry interactive login
      if (status === 401 || status === 403) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('user')
        setIsLoading(false)
        navigate('/unauthorized', {
          state: {
            message:
              err?.response?.data?.requestMessage ||
              err?.response?.data?.message ||
              'User is not authenticated or does not have access to this application',
          },
        })
        return 'denied'
      }
      setIsLoading(false)
      return false
    }
  }

  const handleMicrosoftLogin = async () => {
    if (isLoading) return

    setIsLoading(true)
    try {
      // Try silent path first when user did NOT explicitly request "different user"
      if (!isManualMethod) {
        const ok = await processActiveAccount()
        if (ok) return
      }

      // Trigger redirect-based interactive login. Page navigates away to AAD;
      // execution does not continue past this call on success.
      await instance.loginRedirect({
        ...loginRequest,
        prompt: isManualMethod ? 'select_account' : undefined,
      })
    } catch (error) {
      console.error('Microsoft login failed:', error)
      if (error.errorCode === 'interaction_in_progress') {
        message.warning('A sign-in is already in progress.')
      } else {
        notification.error({
          message: 'Login Error',
          description:
            error?.response?.data?.message ||
            error?.message ||
            'Microsoft authentication failed. Please try again.',
          style: { background: '#fff', border: '2px solid #ff4d4f' },
          duration: 5,
        })
      }
      setIsLoading(false)
      setLoginMethod('manual')
    }
  }

  const handleManualLogin = async (values) => {
    setIsLoading(true)
    try {
      // Call the login API with email
      const email = values.username.includes('@') ? values.username : values.username + '@bd.ey.com'
      const response = await authService.loginUser(email)
      
      // Save access_token to localStorage for API calls
      if (response.tokenID) {
        localStorage.setItem('access_token', response.tokenID)
      }
      
      // Validate response - show error notification on login page
      if (!response.requestIsSuccess || !response.isAuthenticated || !response.userHasAccess) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('user')
        setIsLoading(false)
        const errorMessage = response.requestMessage || 'User is not authenticated or does not have access to this application'
        navigate('/unauthorized', { state: { message: errorMessage } })
        return
      }
      
      // Create user data object
      const userData = {
        username: response.userEmailID || email,
        name: response.fullName,
        email: response.userEmailID,
        id: response.userGPN,
        token: response.tokenID,
        authType: 'manual',
        role: response.userCurrentRole,
        roles: response.userAppRoles,
      }
      
      dispatch(setUser(userData))
      
      // If coming from "login with different user", do full page reload to clear cached data
      if (isManualMethod) {
        window.location.href = '/dashboard'
      } else {
        navigate('/dashboard')
      }
    } catch (error) {
      console.error('Manual login failed:', error)
      const status = error?.response?.status
      // Access denied from the backend (401/403) - redirect to unauthorized page
      if (status === 401 || status === 403) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('user')
        navigate('/unauthorized', {
          state: {
            message:
              error?.response?.data?.message ||
              'User is not authenticated or does not have access to this application',
          },
        })
        return
      }
      notification.error({
        message: 'Login Error',
        description: error?.response?.data?.message || error?.message || 'Login failed. Please try again.',
        style: {
          background: '#fff',
          border: '2px solid #ff4d4f',
        },
        duration: 5,
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    // If main.jsx already populated the user via handleRedirectPromise,
    // jump straight to the dashboard.
    if (user) {
      navigate('/dashboard', { replace: true })
      return
    }

    // Check if coming from a "login with different user" action
    const params = new URLSearchParams(location.search)
    const method = params.get('method')
    
    if (method === 'manual') {
      setLoginMethod('manual')
      setIsManualMethod(true) // Track that user came from "login with different user"
    } else if (loginMethod === 'auto') {
      // Delay auto-login slightly to avoid interaction_in_progress error
      const timer = setTimeout(() => {
        handleMicrosoftLogin()
      }, 100)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Full-screen loader: shown during the initial auto-login attempt and
  // whenever a Microsoft sign-in redirect is in progress (page is about to
  // navigate away to AAD).
  if (loginMethod === 'auto' || isLoading) {
    const isRedirecting = isLoading && loginMethod !== 'auto'
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#fff',
        }}
      >
        <Card
          style={{ 
            width: 450, 
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            borderRadius: '8px'
          }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <div>
              <Title level={3} style={{ margin: 0 }}>Application Tracker</Title>
              <Text type="secondary">
                {isRedirecting
                  ? 'Redirecting to Microsoft sign-in...'
                  : 'Authenticating with Microsoft...'}
              </Text>
            </div>

            <div style={{ padding: '40px 0' }}>
              <Spin 
                indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
                size="large"
              />
            </div>

            {/* <Text type="secondary" style={{ fontSize: '12px' }}>
              EY-IND-CBS-R-P-O-AppTracker-1.0-UAT
            </Text> */}
          </Space>
        </Card>
      </div>
    )
  }

  // Manual login form
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#fff',
      }}
    >
      <Card
        style={{ 
          width: 450, 
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          borderRadius: '8px'
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ margin: 0 }}>Application Tracker</Title>
            <Text type="secondary">Sign in to your account</Text>
          </div>

          <Button
            type="primary"
            icon={<WindowsOutlined />}
            size="large"
            block
            loading={isLoading}
            onClick={handleMicrosoftLogin}
            style={{
              height: '48px',
              fontSize: '16px',
              fontWeight: 500,
              background: '#000',
              borderColor: '#000',
              color: '#fff',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#FFE600'
              e.currentTarget.style.borderColor = '#FFE600'
              e.currentTarget.style.color = '#000'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#000'
              e.currentTarget.style.borderColor = '#000'
              e.currentTarget.style.color = '#fff'
            }}
          >
            Sign in with Microsoft
          </Button>

          <Divider plain>
            <Text type="secondary">OR</Text>
          </Divider>

          <Form
            form={form}
            name="login"
            onFinish={handleManualLogin}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: 'Please enter your email address' }]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="Email Address" 
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large" 
                block
                loading={isLoading}
                style={{
                  height: '48px',
                  fontSize: '16px',
                  background: '#000',
                  borderColor: '#000',
                  color: '#fff',
                  fontWeight: 500,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#FFE600'
                  e.currentTarget.style.borderColor = '#FFE600'
                  e.currentTarget.style.color = '#000'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#000'
                  e.currentTarget.style.borderColor = '#000'
                  e.currentTarget.style.color = '#fff'
                }}
              >
                Sign in
              </Button>
            </Form.Item>
          </Form>

          {/* <Text type="secondary" style={{ fontSize: '12px', textAlign: 'center', display: 'block' }}>
            EY-IND-CBS-R-P-O-AppTracker-1.0-UAT
          </Text> */}
        </Space>
      </Card>
    </div>
  )
}

export default Login
